// AI-CONTEXT: See docs/features/dashboard-participation-ladder-growth-card.md
import { ResponsiveContainer, Sankey, Tooltip, Layer, Rectangle } from 'recharts'

const DEFAULT_COLOR = '#47acc3'

// Lighten a hex colour towards white by `amount` (0-1) — used so the begin
// column reads a shade softer than the current column.
const lighten = (hex, amount) => {
    const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '')
    if (!m) return hex
    const mix = (c) => Math.round(parseInt(c, 16) + (255 - parseInt(c, 16)) * amount)
    return `rgb(${mix(m[1])}, ${mix(m[2])}, ${mix(m[3])})`
}

/**
 * Custom Sankey node: a coloured bar with the trede label placed on the outer
 * side (begin labels to the left, current labels to the right) so they don't
 * collide with the flow bands.
 */
const LadderNode = ({ x, y, width, height, payload, color }) => {
    const isBegin = payload.side === 'begin'
    const fill = isBegin ? lighten(color, 0.35) : color
    const labelX = isBegin ? x - 6 : x + width + 6
    const anchor = isBegin ? 'end' : 'start'

    return (
        <Layer>
            <Rectangle x={x} y={y} width={width} height={height} fill={fill} fillOpacity={0.95} radius={2} />
            <text
                className='ladder-growth-sankey-label'
                x={labelX}
                y={y + height / 2}
                textAnchor={anchor}
                dominantBaseline='middle'
            >
                Trede {payload.step}
            </text>
        </Layer>
    )
}

/**
 * Custom Sankey link: a smooth band coloured with the primary colour at low
 * opacity so overlapping flows stay legible.
 */
const LadderLink = ({ sourceX, targetX, sourceY, targetY, sourceControlX, targetControlX, linkWidth, color }) => (
    <path
        d={`
            M${sourceX},${sourceY}
            C${sourceControlX},${sourceY} ${targetControlX},${targetY} ${targetX},${targetY}
        `}
        fill='none'
        stroke={color}
        strokeWidth={Math.max(1, linkWidth)}
        strokeOpacity={0.32}
    />
)

/**
 * Sankey of participatieladder movement: each persona flows from its begin
 * trede (left) to its current trede (right). Renders nothing without flows —
 * the card owns the empty state.
 *
 * @param {object} props
 * @param {Array<{name: string, step: number, side: 'begin'|'current'}>} props.nodes
 * @param {Array<{source: number, target: number, value: number}>} props.links
 * @param {string} [props.color] - primary colour for nodes/links
 */
const LadderGrowthSankey = ({ nodes = [], links = [], color = DEFAULT_COLOR }) => {
    if (!Array.isArray(nodes) || nodes.length === 0 || !Array.isArray(links) || links.length === 0) {
        return null
    }

    return (
        <ResponsiveContainer width='100%' height='100%'>
            <Sankey
                data={{ nodes, links }}
                node={<LadderNode color={color} />}
                link={<LadderLink color={color} />}
                nodeWidth={12}
                nodePadding={26}
                iterations={0}
                margin={{ top: 12, right: 64, bottom: 12, left: 64 }}
            >
                <Tooltip
                    formatter={(value, _name, entry) => {
                        // Link tooltip: value = persona count; node tooltip omits source/target.
                        const label = entry?.payload?.payload?.name
                        return label ? [`${value} medewerker(s)`, label] : [`${value} medewerker(s)`, '']
                    }}
                />
            </Sankey>
        </ResponsiveContainer>
    )
}

export default LadderGrowthSankey
